import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { X, Plus } from 'lucide-react'; // Added Plus icon

const CreateProduct = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    
    // NEW FIELDS
    const [selectedMainTech, setSelectedMainTech] = useState('');
    const [mainTechList, setMainTechList] = useState([]);
    const [ytLink, setYtLink] = useState('');
    
    // --- TECH STACK TAGS LOGIC ---
    const [techStack, setTechStack] = useState([]); 
    const [tagInput, setTagInput] = useState('');   
    const [suggestedTags, setSuggestedTags] = useState([]); // Store DB tags

    // Files
    const [imageFile, setImageFile] = useState(null); 
    const [galleryFiles, setGalleryFiles] = useState([]); 
    const [pdfFile, setPdfFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);

    // Price Field
    const [price, setPrice] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState('');
    const navigate = useNavigate();
    const quillRef = useRef(null);

    const [isVideo, setIsVideo] = useState(false);
    const [isForm, setIsForm] = useState(false);
    const [paymentLink, setPaymentLink] = useState('');
    const [liveLink, setLiveLink] = useState("")
    const [linkName, setLinkName] = useState('');
    const [link, setLink] = useState('');

    const inputStyles = "bg-gray-900 border border-gray-700 text-gray-200 p-3 rounded-lg w-full focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 shadow-sm";
    const labelStyles = "block text-gray-300 text-sm font-semibold mb-2";

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [catRes, techRes, tagsRes] = await Promise.all([
                    axiosInstance.get('/categories'),
                    axiosInstance.get('/main-tech'),
                    axiosInstance.get('/products/tech-stacks') // Fetch Suggestions
                ]);
                setCategories(catRes.data);
                setMainTechList(techRes.data);
                setSuggestedTags(tagsRes.data);
            } catch (err) {
                console.error("Failed to fetch initial data", err);
            }
        };
        fetchInitialData();
    }, []);

    // --- TAG HANDLERS ---
    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag(tagInput);
        }
        if (e.key === 'Backspace' && !tagInput && techStack.length > 0) {
            setTechStack(techStack.slice(0, -1));
        }
    };

    const addTag = (tag) => {
        const newTag = tag.trim();
        if (newTag && !techStack.includes(newTag)) {
            setTechStack([...techStack, newTag]);
            setTagInput('');
        }
    };

    const removeTag = (indexToRemove) => {
        setTechStack(techStack.filter((_, index) => index !== indexToRemove));
    };

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            const formData = new FormData();
            formData.append('image', file);
            try {
                const response = await axiosInstance.post('/products/upload-image-cloudinary', formData);
                const imageUrl = response.data.url;
                const quill = quillRef.current.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', imageUrl);
            } catch (error) {
                console.error('Quill image upload failed', error);
            }
        };
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                ['link', 'image']
            ],
            handlers: { image: imageHandler },
        },
    }), []);

    const handleGalleryChange = (e) => {
        if (e.target.files) {
            setGalleryFiles(Array.from(e.target.files));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        let finalImageUrl = null;
        let finalPdfUrl = null;
        let finalVideoUrl = null;
        let finalGalleryUrls = [];

        try {
            if (imageFile) {
                setStatus('Uploading featured image...');
                const imageFormData = new FormData();
                imageFormData.append('image', imageFile);
                const imgRes = await axiosInstance.post('/products/upload-image-cloudinary', imageFormData);
                finalImageUrl = imgRes.data.url;
            }

            if (galleryFiles.length > 0) {
                setStatus(`Uploading ${galleryFiles.length} gallery images...`);
                const uploadPromises = galleryFiles.map(file => {
                    const formData = new FormData();
                    formData.append('image', file);
                    return axiosInstance.post('/products/upload-image-cloudinary', formData);
                });
                const responses = await Promise.all(uploadPromises);
                finalGalleryUrls = responses.map(res => res.data.url);
            }

            if (pdfFile) {
                setStatus('Uploading PDF...');
                const pdfFormData = new FormData();
                pdfFormData.append('pdfFile', pdfFile);
                const pdfRes = await axiosInstance.post('/products/upload-pdf-local', pdfFormData);
                finalPdfUrl = pdfRes.data.link;
            }

            if (isVideo && videoFile) {
                setStatus('Uploading Video...');
                const videoFormData = new FormData();
                videoFormData.append('videoFile', videoFile);
                const videoRes = await axiosInstance.post('/products/upload-video-local', videoFormData);
                finalVideoUrl = videoRes.data.link;
            }

            setStatus('Saving product post...');
            const productData = {
                title,
                content,
                category_id: selectedCategory,
                main_tech_id: selectedMainTech || null, 
                yt_link: ytLink || null, 
                tech_stack: techStack.length > 0 ? techStack.join(',') : null, 
                image: finalImageUrl,
                images: finalGalleryUrls,
                pdf_link: finalPdfUrl,
                isVideo: isVideo,
                video_link: finalVideoUrl,
                price: price,
                payment_link: paymentLink || null,
                live_link: liveLink || null,
                form_name: linkName || null,
                form_link: link || null
            };

            await axiosInstance.post('/products', productData);
            navigate('/admin/products-manage');
        } catch (error) {
            console.error('Failed to create product:', error);
            setStatus(`Error: ${error.response?.data?.error || 'An unknown error occurred.'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-2">
            <form onSubmit={handleSubmit} className="bg-gray-800 p-8 rounded-2xl shadow-xl space-y-6">
                <h1 className="text-4xl font-bold mb-8 text-gray-100 text-center">Create a Product</h1>
                
                {/* Title */}
                <div>
                    <label className={labelStyles}>Title</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputStyles} placeholder="Enter title" required />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className={labelStyles}>Category</label>
                        <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={inputStyles + " bg-gray-900"} required>
                            <option value="" disabled>Select a category</option>
                            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelStyles}>Main Tech (Optional)</label>
                        <select value={selectedMainTech} onChange={(e) => setSelectedMainTech(e.target.value)} className={inputStyles + " bg-gray-900"}>
                            <option value="">Select Main Tech</option>
                            {mainTechList.map(tech => <option key={tech.id} value={tech.id}>{tech.name}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className={labelStyles}>YouTube Video Link (Optional)</label>
                    <input type="url" value={ytLink} onChange={(e) => setYtLink(e.target.value)} className={inputStyles} placeholder="https://youtube.com/watch?v=..." />
                </div>

                {/* --- TECH STACK (TAG INPUT & SUGGESTIONS) --- */}
                <div>
                    <label className={labelStyles}>Tech Stack (Tags)</label>
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-2 flex flex-wrap items-center gap-2 focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                        {techStack.map((tag, index) => (
                            <div key={index} className="bg-blue-600/20 text-blue-400 px-2 py-1 rounded-md text-sm flex items-center gap-1 border border-blue-600/30">
                                <span>{tag}</span>
                                <button type="button" onClick={() => removeTag(index)} className="hover:text-red-400 transition-colors"><X size={14} /></button>
                            </div>
                        ))}
                        <input 
                            type="text" 
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            className="bg-transparent text-gray-200 outline-none flex-grow min-w-[150px] p-1"
                            placeholder="Type tag & press Enter..." 
                        />
                    </div>
                    
                    {/* SUGGESTIONS LIST */}
                    {suggestedTags.length > 0 && (
                        <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Used previously (Click to add):</p>
                            <div className="flex flex-wrap gap-2">
                                {suggestedTags
                                    .filter(t => !techStack.includes(t)) // Don't show if already selected
                                    .map((tag, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => addTag(tag)}
                                        className="bg-gray-700/50 hover:bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded border border-gray-600 hover:border-gray-500 transition-colors flex items-center gap-1"
                                    >
                                        <Plus size={10} /> {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div>
                    <label className={labelStyles}>Description / Content</label>
                    <ReactQuill ref={quillRef} theme="snow" value={content} onChange={setContent} modules={modules} className="bg-gray-900 rounded-lg" />
                </div>

                {/* Price */}
                <div>
                    <label className={labelStyles}>Price (Optional)</label>
                    <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={inputStyles} placeholder="0.00" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className={labelStyles}>Featured Image (Main)</label>
                        <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className={inputStyles} />
                    </div>
                    <div>
                        <label className={labelStyles}>Gallery Images</label>
                        <input type="file" accept="image/*" multiple onChange={handleGalleryChange} className={inputStyles} />
                        <p className="text-xs text-gray-400 mt-1">Hold Ctrl/Cmd to select multiple images.</p>
                        {galleryFiles.length > 0 && <p className="text-sm text-blue-400 mt-2">{galleryFiles.length} images selected.</p>}
                    </div>
                </div>

                <div>
                    <label className={labelStyles}>Attach PDF</label>
                    <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className={inputStyles} />
                </div>

                <div>
                    <label className={labelStyles}>Payment Link (Optional)</label>
                    <input type="url" value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)} className={inputStyles} placeholder="https://example.com/payment" />
                </div>
                
                <div>
                    <label className={labelStyles}>Live Link (Optional)</label>
                    <input type="url" value={liveLink} onChange={(e) => setLiveLink(e.target.value)} className={inputStyles} placeholder="https://example.com/payment" />
                </div>

                <div className="p-4 border border-gray-700 rounded-lg bg-gray-900">
                    <label className={labelStyles}>Is this a video post?</label>
                    <div className="flex items-center space-x-6 text-gray-200">
                        <label className="cursor-pointer flex items-center space-x-2"><input type="radio" name="isVideo" checked={isVideo === true} onChange={() => setIsVideo(true)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" /><span>Yes</span></label>
                        <label className="cursor-pointer flex items-center space-x-2"><input type="radio" name="isVideo" checked={isVideo === false} onChange={() => setIsVideo(false)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" /><span>No</span></label>
                    </div>
                </div>

                {isVideo && (
                    <div className="space-y-4 animate-fade-in">
                        <div>
                            <label className={labelStyles}>Upload Video File</label>
                            <input type="file" accept="video/mp4,video/x-m4v,video/*" onChange={(e) => setVideoFile(e.target.files[0])} className={inputStyles} required={isVideo} />
                            <p className="text-xs text-gray-500 mt-1">Supported formats: MP4, AVI, MOV.</p>
                        </div>
                    </div>
                )}

                <div className="p-4 border border-gray-700 rounded-lg bg-gray-900">
                    <label className={labelStyles}>Is there a link ?</label>
                    <div className="flex items-center space-x-6 text-gray-200">
                        <label className="cursor-pointer flex items-center space-x-2"><input type="radio" name="isForm" checked={isForm === true} onChange={() => setIsForm(true)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" /><span>Yes</span></label>
                        <label className="cursor-pointer flex items-center space-x-2"><input type="radio" name="isForm" checked={isForm === false} onChange={() => setIsForm(false)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" /><span>No</span></label>
                    </div>
                </div>

                {isForm && (
                    <div className="space-y-4 animate-fade-in">
                        <div><label className={labelStyles}>Name</label><input type="text" value={linkName} onChange={(e) => setLinkName(e.target.value)} className={inputStyles} placeholder="Name" required /></div>
                        <div><label className={labelStyles}>Link</label><input type="url" value={link} onChange={(e) => setLink(e.target.value)} className={inputStyles} placeholder="https://example.com/link" required /></div>
                    </div>
                )}

                <button type="submit" disabled={isLoading} className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-indigo-500 hover:to-blue-500 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading ? status : 'Create Product'}
                </button>

                {isLoading && status && <p className="text-center text-gray-300 mt-2">{status}</p>}
            </form>
            <style>{`
                [contentEditable][data-placeholder]:empty:before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; display: block; }
                @keyframes fade-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default CreateProduct;